import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, Alert } from 'react-native';
import { NavigationTab, ChildNavProps } from '../types/navigation';
import { HomeView } from './views/HomeView';
import { MilestonesView } from './views/MilestonesView';
import { WellVisitView } from './views/WellVisitView';
import { ProGroupsClientsView } from './views/ProGroupsClientsView';
import { ManageProfilesView } from './views/ManageProfilesView';
import { InitialSetupView } from './views/InitialSetupView';
import { createStoresWithStorage } from '../stores/childNavStores';
import { createcontentService } from '../services/ContentService';
import { AdminSegmentStorage } from '../services/AdminSegmentStorage';
import { AdminAssessmentStorage } from '../services/AdminAssessmentStorage';
import { StoreProvider } from '../contexts/StoreContext';
import { BundledContentInitializer } from '../utils/platformBundleLoaders';
import { useBundledContent } from '../hooks/useBundledContent';
import { useMilestoneSync } from '../hooks/useMilestoneSync';
import { API_CONSTANTS } from '../config/constants';

// Development credentials from environment variables
const DEV_CREDENTIALS = {
  Email: process.env.TEST_USER_EMAIL || '',
  Password: process.env.TEST_USER_PASSWORD || '',
  Signature: 'testsignature123',
  AccessType: API_CONSTANTS.DEFAULT_ACCESS_TYPE,
  SkinType: '1',
};

const isDevelopmentMode = (): boolean => {
  return typeof __DEV__ !== 'undefined' ? __DEV__ : process.env.NODE_ENV === 'development';
};

const getDevCredentials = () => {
  return null; // Hard-coded credentials removed
};

const isDevLoggingEnabled = (): boolean => {
  return isDevelopmentMode();
};

const LegacyTabRemovedView: React.FC<{ label: string }> = ({ label }) => (
  <View style={styles.legacyTabRemovedContainer}>
    <Text style={styles.legacyTabRemovedTitle}>{label} is not available in Pro Groups.</Text>
    <Text style={styles.legacyTabRemovedHint}>Use the Pro Groups Account and Clients views instead.</Text>
  </View>
);

export const ChildNav: React.FC<ChildNavProps> = ({ 
  initialTab = 'ChildNav',
  storage,
  apiService,
  iapService,
  clientCacheStorage,
}) => {
  const [activeTab, setActiveTab] = useState<NavigationTab>(initialTab);
  const [selectedMilestonesDomain, setSelectedMilestonesDomain] = useState<string | null>(null);
  const [selectedConcernsDomain, setSelectedConcernsDomain] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showInitialSetup, setShowInitialSetup] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [storeRehydrationComplete, setStoreRehydrationComplete] = useState(false);
  const [currentProfile, setCurrentProfile] = useState<any>(null);
  const initializingRef = useRef(false);
  const [debugInfo, setDebugInfo] = useState<string[]>([]);
  const [debugSettings, setDebugSettings] = useState({
    showDebugInfo: false,
    enableEditPageLogic: false,
    enableEditCalcLogic: false,
    enableEditReportLogic: false,
  });
  
  // Navigation function that can handle domain selection for MilestonesView
  const [selectedActionsGroup, setSelectedActionsGroup] = React.useState<string | undefined>(undefined);
  const [snapshotReturnPending, setSnapshotReturnPending] = React.useState(false);
  const [wellVisitInitialTab, setWellVisitInitialTab] = React.useState<'Snapshot' | undefined>(undefined);

  React.useEffect(() => {
    if (!['Milestones', 'Concerns', 'Actions'].includes(activeTab)) {
      setSnapshotReturnPending(false);
    }
    if (activeTab !== 'Well-Visit') {
      setWellVisitInitialTab(undefined);
    }
  }, [activeTab]);

  const navigateFromSnapshot = (tab: NavigationTab) => { setSnapshotReturnPending(true); setActiveTab(tab); };
  const handleBackToSnapshot = () => { setSnapshotReturnPending(false); setWellVisitInitialTab('Snapshot'); setActiveTab('Well-Visit'); };

  const navigateWithDomain = (tab: NavigationTab, domain?: string) => {
    if (domain) {
      if (tab === 'Concerns') setSelectedConcernsDomain(domain);
      else if (tab === 'Actions') setSelectedActionsGroup(domain);
      else setSelectedMilestonesDomain(domain);
    }
    if (isDevLoggingEnabled()) {
      console.log('🧭 ChildNav: Navigation requested to tab:', tab, domain ? `with domain: ${domain}` : '');
    }
    setActiveTab(tab);
  };
  
  // Memoize stores creation to prevent unnecessary re-creation
  const stores = React.useMemo(() => {
    if (!storage) return null;
    
    // Debug storage consistency - add to visual debug
    const debugMsg = `🔧 Stores creation: Storage=${storage?.constructor?.name}, Type=${typeof storage}`;
    console.log('🔧 ChildNav stores creation:');
    console.log('  - Storage available:', !!storage);
    console.log('  - Storage type:', typeof storage);
    console.log('  - Storage constructor:', storage ? storage.constructor.name : 'null');
    console.log('  - Storage hash:', storage && storage.toString ? storage.toString().substring(0, 50) : 'no toString');
    setDebugInfo(prev => [...prev, debugMsg].slice(-10)); // Keep last 10 messages
    
    // CRITICAL: Validate storage before using it
    if (storage.constructor.name === 'Object' || !storage.getItem || !storage.setItem) {
      console.error('❌ Invalid storage passed to ChildNav, this will cause corruption');
      setDebugInfo(prev => [...prev, '❌ Invalid storage detected'].slice(-10));
      // Don't create stores with invalid storage
      return null;
    }
    
    return createStoresWithStorage(storage);
  }, [storage]);

  // Debug active tab changes
  React.useEffect(() => {
    if (isDevLoggingEnabled()) {
      console.log('🧭 ChildNav: Active tab changed to:', activeTab, {
        storeRehydrationComplete,
        currentProfile: currentProfile ? { profileId: currentProfile.profileId, accountId: currentProfile.accountId } : null,
        hasStores: !!stores
      });
    }
  }, [activeTab, storeRehydrationComplete, currentProfile, stores]);

  // Monitor store rehydration completion using store subscription
  React.useEffect(() => {
    if (!stores?.useProfileStore) return;

    let hasDetectedRehydration = false;

    // Subscribe to profile store to detect when rehydration completes.
    // Zustand's persist middleware calls setState() after hydrating from storage,
    // which fires all subscribers.  The onRehydrateStorage callback also calls
    // set({ appProfileStorage }) which triggers this subscription on every start.
    const unsubscribe = stores.useProfileStore.subscribe((state: any) => {
      if (!hasDetectedRehydration && !storeRehydrationComplete) {
        if (isDevLoggingEnabled()) {
          console.log('🔄 Store state change detected, profiles count:', state.profiles.length);
        }
        hasDetectedRehydration = true;
        setStoreRehydrationComplete(true);
      }
    });

    // Removed the unconditional "immediate fallback" that used to fire here.
    // That fallback set storeRehydrationComplete=true before the async storage
    // read had returned, so initializeApp ran with profiles:[] and the app
    // showed "Add New Child Profile" even when profiles were stored correctly.
    // The subscription above catches the real rehydration signal, and the
    // 1-second timeout below catches the edge case where storage is empty
    // (no state change is emitted by Zustand when there is nothing to rehydrate).

    // Final safety timeout — avoids an infinite loading state on a genuinely
    // fresh install where AsyncStorage is empty and no state change is emitted.
    const timeout = setTimeout(() => {
      if (!storeRehydrationComplete) {
        if (isDevLoggingEnabled()) {
          console.log('🔄 Store rehydration timeout reached, proceeding with initialization');
        }
        setStoreRehydrationComplete(true);
      }
    }, 1500); // give async storage a generous window before giving up

    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, [stores, storeRehydrationComplete]);

  // Monitor current active profile changes
  React.useEffect(() => {
    if (!stores?.useProfileStore) {
      console.log('👤 ChildNav: No useProfileStore available');
      return;
    }

    const unsubscribe = stores.useProfileStore.subscribe((state: any) => {
      const activeProfile = state.profiles.find((p: any) => p.id === state.activeProfileId);
      if (isDevLoggingEnabled()) {
        console.log('👤 ChildNav: Active profile changed:', {
          activeProfileId: state.activeProfileId,
          profilesCount: state.profiles.length,
          foundProfile: activeProfile,
          allProfiles: state.profiles.map((p: any) => ({ profileId: p.id, accountId: p.accountnum }))
        });
      }
      setCurrentProfile(activeProfile || null);
    });

    // Set initial profile state
    const currentState = stores.useProfileStore.getState();
    if (currentState) {
      const activeProfile = currentState.profiles.find((p: any) => p.id === currentState.activeProfileId);
      if (isDevLoggingEnabled()) {
        console.log('👤 ChildNav: Initial active profile:', {
          activeProfileId: currentState.activeProfileId,
          profilesCount: currentState.profiles.length,
          foundProfile: activeProfile,
          allProfiles: currentState.profiles.map((p: any) => ({ profileId: p.id, accountId: p.accountnum }))
        });
      }
      setCurrentProfile(activeProfile || null);
    } else {
      console.log('👤 ChildNav: No currentState available from useProfileStore');
    }

    return unsubscribe;
  }, [stores]);

  // Additional effect to ensure profile is set when store rehydration completes
  React.useEffect(() => {
    if (storeRehydrationComplete && stores?.useProfileStore && !currentProfile) {
      const currentState = stores.useProfileStore.getState();
      if (currentState) {
        const activeProfile = currentState.profiles.find((p: any) => p.id === currentState.activeProfileId);
        if (isDevLoggingEnabled()) {
          console.log('👤 ChildNav: Setting profile after rehydration completion:', {
            activeProfileId: currentState.activeProfileId,
            profilesCount: currentState.profiles.length,
            foundProfile: activeProfile,
            allProfiles: currentState.profiles.map((p: any) => ({ profileId: p.id, accountId: p.accountnum }))
          });
        }
        setCurrentProfile(activeProfile || null);
      }
    }
  }, [storeRehydrationComplete, stores, currentProfile]);
  
  // Set API service in the stores if both are available
  React.useEffect(() => {
    if (stores && apiService && stores.setApiService) {
      if (isDevLoggingEnabled()) {
        console.log('🔧 Setting API service in userStore...');
      }
      stores.setApiService(apiService);
    }
  }, [stores, apiService]);

  // Expose cache adapter globally so upcoming Clients views can consume it
  // without changing existing legacy view prop surfaces.
  React.useEffect(() => {
    if (!clientCacheStorage) {
      return;
    }

    (globalThis as any).__proGroupsClientCacheStorage = clientCacheStorage;
    (globalThis as any).__childNavClientCacheStorage = clientCacheStorage;
  }, [clientCacheStorage]);
  
  // Load debug settings from storage
  React.useEffect(() => {
    if (!storage) return;
    
    const loadDebugSettings = async () => {
      try {
        const showDebugInfo = (await storage.getItem('debug_showDebugInfo')) === 'true';
        const enableEditPageLogic = (await storage.getItem('debug_enableEditPageLogic')) === 'true';
        const enableEditCalcLogic = (await storage.getItem('debug_enableEditCalcLogic')) === 'true';
        const enableEditReportLogic = (await storage.getItem('debug_enableEditReportLogic')) === 'true';
        
        setDebugSettings({
          showDebugInfo,
          enableEditPageLogic,
          enableEditCalcLogic,
          enableEditReportLogic,
        });
        
        if (isDevLoggingEnabled()) {
          console.log('🐛 ChildNav: Loaded debug settings:', {
            showDebugInfo,
            enableEditPageLogic,
            enableEditCalcLogic,
            enableEditReportLogic,
          });
        }
      } catch (error) {
        console.error('🐛 ChildNav: Failed to load debug settings:', error);
      }
    };
    
    loadDebugSettings();
  }, [storage]);
  
  const contentStore = stores?.useContentStore || null;
  const contentService = React.useMemo(() => {
    return (stores && apiService && stores.useContentStore) ? 
      createcontentService(apiService, stores.useContentStore.getState()) : null;
  }, [stores, apiService]);

  // Memoize the contentStore to prevent unnecessary re-renders
  const stableContentStore = React.useMemo(() => contentStore, [contentStore]);

  // Initialize bundled content at component level (not inside initializeApp to avoid hook violations)
  const { status: bundleStatus, initializeBundledContent } = useBundledContent(
    storage, 
    stableContentStore, 
    !!storage && !!stableContentStore  // Auto-initialize when storage and contentStore are available
  );

  // Log bundled content status in development
  useEffect(() => {
    if (isDevLoggingEnabled() && bundleStatus) {
      const statusMsg = `📦 Bundle: loaded=${bundleStatus.isLoaded}, loading=${bundleStatus.isLoading}`;
      console.log('📦 Bundled content status:', {
        isLoaded: bundleStatus.isLoaded,
        isLoading: bundleStatus.isLoading,
        error: bundleStatus.error,
        version: bundleStatus.version,
        lastLoadedAt: bundleStatus.lastLoadedAt
      });
      setDebugInfo(prev => [...prev, statusMsg].slice(-10));
    }
  }, [bundleStatus]); // Track all status changes for better debugging

  // Keep milestone storage in sync whenever bundled content loads or its version changes.
  // Standard milestones come from AdminSegment 11255; premium from AdminSegment 11270.
  // Pass contentService so the hook can use the API-backed service when available (same
  // path as DebugView's working reload), falling back to bundled ContentStore records.
  useMilestoneSync(storage, stableContentStore, bundleStatus, contentService ?? undefined);

  // Initialize authentication when the app starts
  useEffect(() => {
    // Debug logging for effect execution
    if (isDevLoggingEnabled()) {
      console.log('🎯 ChildNav useEffect triggered...');
      console.log(`   Storage available: ${!!storage}`);
      console.log(`   API Service available: ${!!apiService}`);
      console.log(`   Development mode: ${isDevelopmentMode()}`);
      console.log(`   Is initialized: ${isInitialized}`);
    }

    // Skip if already initialized or currently initializing
    if (isInitialized || initializingRef.current) {
      if (isDevLoggingEnabled()) {
        console.log(`ℹ️ Skipping initialization - initialized: ${isInitialized}, in-progress: ${initializingRef.current}`);
      }
      return;
    }

    const initializeApp = async () => {
      initializingRef.current = true;
      try {
        if (isDevLoggingEnabled()) {
          console.log('🚀 ChildNav: Starting app initialization...');
        }
        
        if (!storage) {
          if (isDevLoggingEnabled()) {
            console.log('⚠️ No storage available, showing initial setup');
          }
          setShowInitialSetup(true);
          setIsCheckingAuth(false);
          setIsInitialized(true);
          return;
        }

        // Wait for store rehydration to complete before making navigation decisions
        if (!storeRehydrationComplete && stores) {
          if (isDevLoggingEnabled()) {
            console.log('⏳ Waiting for store rehydration to complete...');
          }
          initializingRef.current = false; // Reset so we can try again when rehydration completes
          return; // Exit early and wait for rehydration
        }

        if (isDevLoggingEnabled() && storeRehydrationComplete) {
          console.log('✅ Store rehydration complete, proceeding with navigation decisions...');
        }

        // Check for existing authentication first
        const storedToken = await storage.getItem('auth_token');
        const storedEmail = await storage.getItem('user_email');
        const userSession = await storage.getItem('userSession');

        if (isDevLoggingEnabled()) {
          console.log('🔍 Checking authentication state...');
          console.log(`   Stored token exists: ${!!storedToken}`);
          console.log(`   Stored email exists: ${!!storedEmail}`);
          console.log(`   Stored user session exists: ${!!userSession}`);
        }

        // Check for existing profiles using the rehydrated store state (now available and reliable)
        let hasExistingProfiles = false;
        if (stores && stores.useProfileStore && storeRehydrationComplete) {
          const profileState = stores.useProfileStore.getState();
          hasExistingProfiles = profileState.profiles.length > 0;
          
          if (isDevLoggingEnabled()) {
            console.log(`🔍 Rehydrated profile store check: Found ${profileState.profiles.length} existing profiles`);
          }
          setDebugInfo(prev => [...prev, `🔍 Found ${profileState.profiles.length} profiles in store`].slice(-10));
        }

        if (isDevLoggingEnabled()) {
          console.log(`🔍 Profile check: ${hasExistingProfiles ? 'Found existing profiles' : 'No existing profiles found'}`);
        }
        setDebugInfo(prev => [...prev, `🔍 Profiles exist: ${hasExistingProfiles}`].slice(-10));

        // If we have stored authentication, proceed with normal initialization
        if (storedToken || userSession) {
          if (isDevLoggingEnabled()) {
            console.log('✅ Found existing authentication, proceeding with normal initialization');
          }
          
          // Set token in API service if available
          if (apiService && storedToken) {
            apiService.setAuthToken(storedToken);
          }
          
          setIsCheckingAuth(false);
        } else if (hasExistingProfiles) {
          // No authentication but have existing profiles - skip initial setup and continue in guest mode
          if (isDevLoggingEnabled()) {
            console.log('✅ No authentication but found existing profiles, continuing in guest mode');
          }
          setIsCheckingAuth(false);
        } else {
          // No existing authentication and no profiles found - show initial setup
          if (isDevLoggingEnabled()) {
            console.log('ℹ️ No existing authentication or profiles found, showing initial setup screen');
          }
          setShowInitialSetup(true);
          setIsCheckingAuth(false);
        }

        // Initialize AdminAssessment storage with pre-populated data
        if (storage) {
          if (isDevLoggingEnabled()) {
            console.log('🗂️ Initializing AdminAssessment storage...');
          }
          try {
            const assessmentStorage = new AdminAssessmentStorage(storage);
            await assessmentStorage.initialize();
            if (isDevLoggingEnabled()) {
              console.log('✅ AdminAssessment storage initialized with default assessments');
            }
          } catch (error) {
            console.error('⚠️ Failed to initialize AdminAssessment storage:', error);
          }
        }

        // Initialize milestone data from bundled AdminSegment content after bundled content loading is complete
        // NOTE: This is now handled by separate useEffect that watches bundleStatus.isLoaded
        // Milestone initialization moved to reactive hook to handle async bundled content loading timing
        
        // Initialize bundled content after all other initialization
        // NOTE: Now handled by useBundledContent hook above - this manual approach is disabled
        // if (storage && stableContentStore) {
        //   try {
        //     if (isDevLoggingEnabled()) {
        //       console.log('🚀 Initializing bundled content...');
        //     }
        //     
        //     const initializer = new BundledContentInitializer(storage, stableContentStore);
        //     
        //     // Check if initialization is needed
        //     const shouldInit = await initializer.shouldInitialize();
        //     
        //     if (shouldInit) {
        //       await initializer.initialize();
        //       console.log('🚀 Bundled content initialization complete');
        //     } else {
        //       console.log('🚀 Bundled content already loaded');
        //     }
        //   } catch (bundleError) {
        //     console.error('🚀 Bundled content initialization failed:', bundleError);
        //   }
        // }

        if (isDevLoggingEnabled()) {
          console.log('✅ App initialization completed');
        }
        setIsInitialized(true);
        initializingRef.current = false;
        
      } catch (error) {
        console.error('❌ Failed to initialize ChildNav:', error);
        if (isDevLoggingEnabled()) {
          console.log('📊 Error details:', (error as any)?.stack || String(error));
        }
        // Still set as initialized so the app can run in basic mode
        setIsInitialized(true);
        initializingRef.current = false;
      }
    };

    initializeApp();
  }, [storage, apiService, storeRehydrationComplete]); // Re-run if storage, apiService, or rehydration state changes

  // Bundled content initialization now happens inside initializeApp function
  // to avoid "Invalid hook call" errors

  // Handle initial setup completion
  const handleSetupComplete = async (
    setupType: 'new-account' | 'existing-account' | 'guest-mode', 
    credentials?: { email: string; password: string }
  ) => {
    if (isDevLoggingEnabled()) {
      console.log(`🔧 Initial setup completed: ${setupType}`);
      if (credentials) {
        console.log(`   Email: ${credentials.email}`);
      }
    }

    try {
      if (setupType === 'guest-mode') {
        // Continue without authentication - just hide setup screen
        if (isDevLoggingEnabled()) {
          console.log('✅ Continuing in guest mode (local only)');
        }
        setShowInitialSetup(false);
        setIsInitialized(true);
        return;
      }

      // Handle account creation or login
      if (credentials && apiService && storage) {
        if (setupType === 'existing-account') {
          // Attempt login with existing account via the single shared login path —
          // this establishes the full userStore session (Zustand state + all
          // AUTH_STORAGE_KEYS), not just a bare token, so it's visible to
          // userStore.session.isAuthenticated and eligible for proactive/reactive
          // token refresh like any other login.
          try {
            if (!stores?.useUserStore) {
              throw new Error('User store not available');
            }

            const loginSucceeded = await stores.useUserStore.getState().login(
              credentials.email,
              credentials.password,
              credentials.email,
              '',
              API_CONSTANTS.DEFAULT_ACCESS_TYPE,
              '1'
            );

            if (loginSucceeded) {
              if (isDevLoggingEnabled()) {
                console.log('✅ Login successful');
              }

              setShowInitialSetup(false);
              setIsInitialized(true);
            } else {
              throw new Error('Invalid login response');
            }
          } catch (loginError) {
            console.error('❌ Login failed:', loginError);
            Alert.alert('Login Error', 'Login failed. Please check your credentials and try again.');
          }
        } else if (setupType === 'new-account') {
          // For now, treat new account creation the same as existing login
          // In a real implementation, this would create a new account first
          if (isDevLoggingEnabled()) {
            console.log('ℹ️ New account creation not implemented, proceeding in guest mode');
          }
          setShowInitialSetup(false);
          setIsInitialized(true);
        }
      } else {
        // Missing required services, continue in guest mode
        if (isDevLoggingEnabled()) {
          console.log('⚠️ Missing API service or storage, continuing in guest mode');
        }
        setShowInitialSetup(false);
        setIsInitialized(true);
      }
    } catch (error) {
      console.error('❌ Setup completion failed:', error);
      Alert.alert('Setup Error', 'Setup failed. Please try again.');
    }
  };

  const renderCurrentView = () => {
    switch (activeTab) {
      case 'Clients':
        return <ProGroupsClientsView storage={storage} apiService={apiService} clientCacheStorage={clientCacheStorage} />;
      case 'ChildNav':
        return <HomeView onTabChange={setActiveTab} activeTab={activeTab} onNavigateWithDomain={navigateWithDomain} storage={storage} apiService={apiService} contentService={contentService} useContentStore={contentStore} />;
      case 'Milestones':
        return <MilestonesView selectedDomain={selectedMilestonesDomain} onDomainSelected={() => setSelectedMilestonesDomain(null)} showBackToSnapshot={snapshotReturnPending} onBackToSnapshot={handleBackToSnapshot} onUpdateConcerns={() => setActiveTab('Concerns')} />;
      case 'Concerns':
        return <LegacyTabRemovedView label="Concerns" />;
      case 'Well-Visit':
        // Don't render WellVisitView until we have profile information or confirmed no profiles
        if (!stores?.useProfileStore) {
          console.log('🏥 ChildNav: No profile store available, showing loading...');
          return <View style={styles.container}><Text style={{ color: '#ffffff', textAlign: 'center', marginTop: 100 }}>Loading profile information...</Text></View>;
        }
        
        const profileState = stores.useProfileStore.getState();
        if (!profileState) {
          console.log('🏥 ChildNav: No profile state available, showing loading...');
          return <View style={styles.container}><Text style={{ color: '#ffffff', textAlign: 'center', marginTop: 100 }}>Loading profile information...</Text></View>;
        }

        // Wait for store rehydration to complete
        if (!storeRehydrationComplete) {
          console.log('🏥 ChildNav: Store rehydration not complete, showing loading...');
          return <View style={styles.container}><Text style={{ color: '#ffffff', textAlign: 'center', marginTop: 100 }}>Loading profile information...</Text></View>;
        }

        if (isDevLoggingEnabled()) {
          console.log('🏥 ChildNav: Rendering WellVisitView with profile:', {
            profileId: currentProfile?.profileId || 'default_profile',
            accountId: currentProfile?.accountId || 'default_account',
            currentProfile: currentProfile,
            activeProfileId: profileState.activeProfileId,
            profilesCount: profileState.profiles.length
          });
        }
        return (
          <WellVisitView 
            useContentStore={contentStore} 
            storage={storage} 
            apiService={apiService} 
            contentService={contentService}
            profileId={currentProfile?.id || 'default_profile'}
            accountId={currentProfile?.accountnum || 'default_account'}
            showDebugInfo={debugSettings.showDebugInfo}
            enableEditPageLogic={debugSettings.enableEditPageLogic}
            enableEditCalcLogic={debugSettings.enableEditCalcLogic}
            enableEditReportLogic={debugSettings.enableEditReportLogic}
            initialTab={wellVisitInitialTab}
            onTabChange={setActiveTab}
            onSnapshotTabChange={navigateFromSnapshot}
          />
        );
      case 'Activities':
        return <LegacyTabRemovedView label="Activities" />;
      case 'Guidance':
        return <LegacyTabRemovedView label="Guidance" />;
      case 'ResourceList':
        return <LegacyTabRemovedView label="Resource List" />;
      case 'Actions':
        return <LegacyTabRemovedView label="Actions" />;
      case 'ManageProfiles':
        return <ManageProfilesView onTabChange={setActiveTab} />;
      case 'Connections':
        return <LegacyTabRemovedView label="Connections" />;
      case 'ReferAFriend':
        return <LegacyTabRemovedView label="Refer A Friend" />;
      case 'Account':
        return <LegacyTabRemovedView label="Account" />;
      case 'Settings':
        return <LegacyTabRemovedView label="Settings" />;
      case 'More':
        return <LegacyTabRemovedView label="More" />;
      default:
        return <HomeView onTabChange={setActiveTab} activeTab={activeTab} onNavigateWithDomain={navigateWithDomain} storage={storage} apiService={apiService} contentService={contentService} useContentStore={contentStore} />;
    }
  };

  // Show loading screen if still initializing
  if (isCheckingAuth || !isInitialized) {
    return (
      <View style={styles.container}>
        <Text style={{ 
          color: '#ffffff', 
          textAlign: 'center', 
          marginTop: 100,
          fontSize: 18 
        }}>
          {!storeRehydrationComplete ? 'Loading stores...' : 
           isCheckingAuth ? 'Checking authentication...' : 
           'Initializing app...'}
        </Text>
      </View>
    );
  }

  // Show initial setup if needed
  if (showInitialSetup) {
    return (
      <View style={styles.container}>
        <InitialSetupView 
          onSetupComplete={handleSetupComplete}
          storage={storage}
          apiService={apiService}
        />
      </View>
    );
  }

  // Show loading state while checking authentication
  if (isCheckingAuth) {
    return (
      <View style={styles.container}>
        <View style={[styles.content, { justifyContent: 'center', alignItems: 'center' }]}>
          {/* You could add a loading spinner here if desired */}
        </View>
      </View>
    );
  }

  // If no storage, render without provider (fallback mode)
  if (!storage) {
    return (
      <View style={styles.container}>
        <View style={styles.content}>
          {renderCurrentView()}
        </View>
      </View>
    );
  }

  return (
    <StoreProvider storage={storage} apiService={apiService}>
      <View style={styles.container}>
        <View style={styles.content}>
          {renderCurrentView()}
        </View>
      </View>
    </StoreProvider>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  content: {
    flex: 1,
  },
  legacyTabRemovedContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
  },
  legacyTabRemovedTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#e2e8f0',
    textAlign: 'center',
    marginBottom: 8,
  },
  legacyTabRemovedHint: {
    fontSize: 13,
    color: '#94a3b8',
    textAlign: 'center',
  },
});